import { IImage } from '@faire/web-api--source/indigofair/data/IImage';

export namespace IFilterOption {
  /**
   * A list of decorations that the frontend should use for select FilterOptions.
   * This enables decorations for things like season-based promotions or for the inception use-case of
   * highlighting preorder seasons as part of Evergreen Preorder Visibility.
   */
  export enum Customizations {
    CUSTOMIZATIONS_UNKNOWN = 'CUSTOMIZATIONS_UNKNOWN',
    /**
     * For evergreen preorder seasons - decorate in side nav with "New!"
     */
    EVERGREEN_NEW = 'EVERGREEN_NEW',
    /**
     * For faire fashion week preorder options - decorate in side nav with twinkle icon
     */
    FAIRE_FASHION_WEEK = 'FAIRE_FASHION_WEEK',
  }

  export enum SelectionState {
    SELECTION_STATE_UNKNOWN = 'SELECTION_STATE_UNKNOWN',
    /**
     * The filter option is selected. If it has sub-options, they're all selected too.
     */
    FULLY_SELECTED = 'FULLY_SELECTED',
    /**
     * The filter option is unselected but has selected sub-options.
     */
    PARTIALLY_SELECTED = 'PARTIALLY_SELECTED',
    /**
     * The filter option isn't selected. If it has sub-options, they're all unselected too.
     */
    UNSELECTED = 'UNSELECTED',
  }

  /**
   * Return an instance of IFilterOption with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IFilterOption>): IFilterOption =>
    ({
      sub_options: [],
      customizations: [],
      ...(partial ?? {}),
    }) as IFilterOption;
}

/**
 * A class representing each option for a single section.
 */
export interface IFilterOption {
  /**
   * This is a identifier for this option unique amongst all FilterSections.
   * Frontend will use this to add to the `filterKeys` field on the next request with the options selected.
   * What Section this comes from is not relevant because of the uniqueness of the keys.
   * Technically speaking, it might be:
   * * `brand:` followed by a DbBrand token (`b_xxx`) for the FilterSection BRAND
   * * `category:` followed by a normalized category name (lower case, spaces become underscore) for the filter FilterSection CATEGORY
   * * `wholesale_price:` followed by the enum constant normalized for the FilterSection WHOLESALE_PRICE
   * * `maker_minimum:` followed by the enum constant normalized for the FilterSection MAKER_MINIMUM
   * * `maker_value:` followed by the enum constant normalized for the FilterSection MAKER_VALUE
   * All keys will always be unique but fields can be added or removed on backend deploys
   * (hopefully not during a single request/response cycle), so frontends must be aware of that.
   */
  key: string | undefined;
  /**
   * Short display name for this option to be used within the filter (not necessarily but probably unique).
   * It's an English human-readable description of this option that should be displayed as is by frontends.
   * This can be i18n'ed in the future in the backend but right now it's always in English.
   */
  display_name: string | undefined;
  /**
   * Longer display name for this option to be used in conjunction with other options from different sections.
   * This name is unique amongst all sections so that it can be used in context where different filters are shown
   * together (not in hierarchy).
   * It's an English human-readable description of this option that should be displayed as is by frontends.
   * This can be i18n'ed in the future in the backend but right now it's always in English.
   */
  global_display_name: string | undefined;
  /**
   * This is whether this option was selected or not, meaning, whether it was provided in the `filterKeys` options.
   * This is an information that surely frontend already has but it's returned again as to make implementation easier.
   * Deprecated in favour of selectionState.
   */
  is_selected: boolean | undefined;
  /**
   * This is whether this option was selected or not, meaning, whether it was provided in the `filterKeys` options.
   * This is an information that surely frontend already has but it's returned again as to make implementation easier.
   */
  selection_state: keyof typeof IFilterOption.SelectionState | undefined;
  /**
   * This is a preview of how many results further refining by this category will bring.
   * There is no guarantee that this is actually the exact result, but it's a good enough estimate to be shown
   * on the UI.
   * This field will be ignored from proto compare diff as it has slightly different number in each request
   * @protobufOption (faire.common.diffIgnore) = ALWAYS
   */
  results_count: number | undefined;
  /**
   * Backend can use this option to nest a sub option list into this option.
   * Frontend must be able to display this hierarchically.
   * Currently it's used for CATEGORY and just a few levels, but could go indefinitely deep.
   */
  sub_options: Array<IFilterOption>;
  /**
   * This is an optional identifier of the group of options that this option belongs to.
   */
  group_key: string | undefined;
  customizations: Array<keyof typeof IFilterOption.Customizations>;
  /**
   * This is an optional image that will appear for quick filters in Filter Redesign.
   */
  image: IImage | undefined;
  formatted_results_count: string | undefined;
}
